/** Datos de la clínica (nombre, logo, color, sedes) compartidos por la cabecera, la configuración y los impresos. */
import { useEffect, useState } from "react";
import { api, apiBlob, leerSesion } from "./api.ts";
import { paletaDeAcento, type VariablesTema, type BannerClinica } from "../../dominio/src/index.ts";

export interface Sede { id: string; nombre: string; direccion: string; telefono: string; whatsapp: string; activa: boolean; es_matriz: boolean }
export interface Clinica {
  id: string; nombre: string; razonSocial: string; ruc: string; correo: string; telefono: string; ciudad: string; acento: string; institucion?: string; unicodigo?: string;
  logo: number | null; sedes: Sede[]; banner: BannerClinica;
  /** URL local del logo ya descargado (con la sesión), o null. */
  logoUrl?: string | null;
}

/* La clínica y su logo se guardan en este navegador: al volver a Inicio o al ingresar, el banner sale al instante (sin parpadeo)
   y se actualiza en segundo plano. Solo se usan si son de la clínica de la sesión actual. */
const CLAVE_DATOS = "donna.clinica.datos";
const aDatos = (blob: Blob) => new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsDataURL(blob); });
function restaurar(): { clinica: Clinica; logo: { version: number; url: string } | null } | null {
  try {
    const g = JSON.parse(localStorage.getItem(CLAVE_DATOS) ?? "null") as (Clinica & { logoDatos?: string }) | null;
    const s = leerSesion();
    if (!g || !g.id || !s || s.usuario.clinicaId !== g.id) return null;
    const logo = g.logo !== null && g.logoDatos ? { version: g.logo, url: g.logoDatos } : null;
    return { clinica: { ...g, sedes: g.sedes ?? [], logoUrl: logo?.url ?? null }, logo };
  } catch { return null; }
}
function guardarDatos(c: Clinica) {
  try { localStorage.setItem(CLAVE_DATOS, JSON.stringify({ id: c.id, nombre: c.nombre, acento: c.acento, logo: c.logo, banner: c.banner, sedes: [], logoDatos: c.logoUrl?.startsWith("data:") ? c.logoUrl : undefined })); } catch { /* opcional */ }
}
let actual: Clinica | null = null;
let cargando: Promise<void> | null = null;
const oyentes = new Set<(c: Clinica | null) => void>();
let urlLogo: { version: number; url: string } | null = null;

/** Con la sesión ya guardada: pone de inmediato la última clínica recordada (si es la de esta sesión), antes de pedirla al servidor. */
export function restaurarClinica() {
  if (actual) return;
  const g = restaurar();
  if (!g) return;
  actual = g.clinica; urlLogo = g.logo;
  aplicarAcento(g.clinica.acento);
  oyentes.forEach((f) => f(actual));
}

/**
 * Aplica la paleta de la clínica a toda la app: fondo, tarjetas, líneas, textos y acento, en claro y oscuro.
 * Se recuerda en este navegador para que la pantalla de inicio de sesión ya salga con los colores de la clínica.
 */
export function aplicarAcento(clave: string, recordar = true) {
  const p = paletaDeAcento(clave);
  const reglas = (v: VariablesTema) => Object.entries(v).map(([k, x]) => `${k}:${x}`).join(";");
  let st = document.getElementById("acento-clinica") as HTMLStyleElement | null;
  if (!st) { st = document.createElement("style"); st.id = "acento-clinica"; document.head.appendChild(st); }
  st.textContent = `:root{${reglas(p.claro)}}
@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){${reglas(p.oscuro)}}}
:root[data-theme="dark"]{${reglas(p.oscuro)}}`;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", p.claro["--bg"]);
  if (recordar) try { localStorage.setItem("donna.acento", clave); } catch { /* opcional */ }
}

/** Al abrir la app: los colores de la última clínica usada en este navegador. */
export function acentoInicial() {
  try { const k = localStorage.getItem("donna.acento"); if (k) aplicarAcento(k, false); } catch { /* opcional */ }
}

async function conLogo(c: Clinica): Promise<Clinica> {
  if (c.logo === null) { if (urlLogo?.url.startsWith("blob:")) URL.revokeObjectURL(urlLogo.url); urlLogo = null; return { ...c, logoUrl: null }; }
  if (!urlLogo || urlLogo.version !== c.logo) {
    const url = await apiBlob(`/api/clinica/logo?v=${c.logo}`).catch(() => null);
    // se guarda como dato en línea (no como enlace temporal) para que al volver a Inicio se pinte sin esperar
    const dato = url ? await fetch(url).then((r) => r.blob()).then(aDatos).catch(() => url) : null;
    if (url && dato !== url) URL.revokeObjectURL(url);
    if (urlLogo?.url.startsWith("blob:")) URL.revokeObjectURL(urlLogo.url);
    urlLogo = dato ? { version: c.logo, url: dato } : null;
  }
  return { ...c, logoUrl: urlLogo?.url ?? null };
}

/* ---------- marca en la pantalla de inicio de sesión ---------- */

export interface Marca { id: string; nombre: string; acento: string; logo: number | null; banner: BannerClinica }
const CLAVE_CLINICA = "donna.clinica";

/** Identificador de la clínica para la pantalla de ingreso: el del enlace (?clinica=…) o el último usado en este navegador. */
function clinicaRecordada(): string | null {
  try {
    const u = new URL(location.href), q = u.searchParams.get("clinica");
    if (q && /^[0-9a-f-]{36}$/i.test(q)) {
      localStorage.setItem(CLAVE_CLINICA, q.toLowerCase());
      u.searchParams.delete("clinica");
      history.replaceState(null, "", u.pathname + u.search + u.hash);
    }
    return localStorage.getItem(CLAVE_CLINICA);
  } catch { return null; }
}

/** Nombre, color y logo de la clínica, sin sesión (o null: se muestra la marca de Donna). */
export async function cargarMarca(): Promise<Marca | null> {
  const id = clinicaRecordada();
  try {
    const r = await fetch(`/api/marca${id ? `?c=${encodeURIComponent(id)}` : ""}`);
    const m = r.ok ? ((await r.json()) as { clinica: Marca | null }).clinica : null;
    if (m) { aplicarAcento(m.acento); recordarMarca(m); }
    else try { localStorage.removeItem(CLAVE_MARCA); } catch { /* opcional */ }
    return m;
  } catch { return null; }
}

/* La marca del ingreso también se recuerda (con el logo como dato en línea) para que el banner aparezca de inmediato. */
const CLAVE_MARCA = "donna.marca";
export type MarcaGuardada = Marca & { logoDatos?: string };
export function marcaGuardada(): MarcaGuardada | null {
  try {
    const id = clinicaRecordada(), g = JSON.parse(localStorage.getItem(CLAVE_MARCA) ?? "null") as MarcaGuardada | null;
    return g && g.id && g.id === id ? g : null;
  } catch { return null; }
}
export const hayClinicaRecordada = () => { try { return !!localStorage.getItem(CLAVE_CLINICA); } catch { return false; } };
function recordarMarca(m: Marca) {
  try {
    const previa = JSON.parse(localStorage.getItem(CLAVE_MARCA) ?? "null") as MarcaGuardada | null;
    const logoDatos = previa && previa.id === m.id && previa.logo === m.logo ? previa.logoDatos : undefined;
    localStorage.setItem(CLAVE_MARCA, JSON.stringify({ ...m, logoDatos }));
    if (m.logo !== null && !logoDatos) {
      fetch(`/api/marca/logo?c=${m.id}&v=${m.logo}`).then((r) => (r.ok ? r.blob() : Promise.reject())).then(aDatos)
        .then((d) => localStorage.setItem(CLAVE_MARCA, JSON.stringify({ ...m, logoDatos: d }))).catch(() => undefined);
    }
  } catch { /* opcional */ }
}

export const urlLogoMarca = (m: Marca) => (m.logo === null ? null : `/api/marca/logo?c=${m.id}&v=${m.logo}`);

/** Enlace de ingreso con la marca de la clínica (para instalaciones con varias clínicas). */
export const enlaceIngreso = (id: string) => `${location.origin}/?clinica=${id}`;

/** Publica datos nuevos (por ejemplo, la respuesta de guardar la configuración). */
export async function publicarClinica(c: Clinica) {
  if (c.id) try { localStorage.setItem(CLAVE_CLINICA, c.id); } catch { /* opcional */ }
  actual = await conLogo(c);
  guardarDatos(actual);
  aplicarAcento(actual.acento);
  oyentes.forEach((f) => f(actual));
}

export function recargarClinica(): Promise<void> {
  cargando = api<Clinica>("GET", "/api/clinica").then(publicarClinica).catch(() => undefined).finally(() => { cargando = null; });
  return cargando;
}

export function olvidarClinica() { actual = null; oyentes.forEach((f) => f(null)); }

export function useClinica(): Clinica | null {
  const [c, setC] = useState<Clinica | null>(() => { restaurarClinica(); return actual; });
  useEffect(() => {
    oyentes.add(setC);
    if (!actual && !cargando) recargarClinica();
    return () => { oyentes.delete(setC); };
  }, []);
  return c;
}
